/**
 * useTypeDetection Hook
 * 
 * Automatically detects the semantic type of input text.
 */

import { useEffect, useMemo,useState } from 'react';

import { detectAll,detectType } from '@/detectors';
import type { ISemanticType } from '@/interfaces/ISemanticType';

interface TypeDetectionResult {
  /** Primary detected type */
  type: ISemanticType | null;
  /** All possible types with confidence scores */
  allTypes: Array<{ type: ISemanticType; confidence: number }>;
  /** Whether detection is in progress */
  isDetecting: boolean;
}

/**
 * Debounce function specific to string input.
 * Returns a tuple: [debouncedFn, cancelFn].
 */
function debounceDetect(
  fn: (text: string) => void,
  delay: number
): [(text: string) => void, () => void] {
  let timeoutId: ReturnType<typeof setTimeout>;
  const invoke = (text: string) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(text), delay);
  };
  const cancel = () => clearTimeout(timeoutId);
  return [invoke, cancel];
}

/**
 * Hook to detect the semantic type of input text.
 * Uses debouncing to avoid excessive processing on rapid changes.
 */
export function useTypeDetection(
  input: string,
  debounceMs = 300
): TypeDetectionResult {
  const [result, setResult] = useState<TypeDetectionResult>({
    type: null,
    allTypes: [],
    isDetecting: false,
  });

  // Memoize the debounced detection function
  const [debouncedDetect, cancelDetect] = useMemo(
    () =>
      debounceDetect((text: string) => {
        const type = detectType(text);
        const allTypes = detectAll(text);
        setResult({
          type,
          allTypes,
          isDetecting: false,
        });
      }, debounceMs),
    [debounceMs]
  );

  useEffect(() => {
    if (!input) {
      setResult({ type: null, allTypes: [], isDetecting: false });
      return;
    }

    setResult((prev: TypeDetectionResult) => ({ ...prev, isDetecting: true }));
    debouncedDetect(input);

    return cancelDetect;
  }, [input, debouncedDetect, cancelDetect]);

  return result;
}

